import React from "react";
import { MdPeopleOutline } from "react-icons/md";
import { CapacityTimelineResponse } from "../../../../interfaces/Capacity";

interface Props {
  data: CapacityTimelineResponse | null;
}

interface MonthAgg {
  capacity: number;
  allocated: number;
  worked: number;
  estimated: number;
}

const monthColor = (utilization: number) =>
  utilization >= 100 ? "red" : utilization >= 80 ? "yellow" : "green";

const cellTone = (alloc: number, worked: number, color: string) => {
  if (alloc === 0 && worked === 0) return "bg-gray-50 dark:bg-navy-900";
  if (color === "red") return "bg-red-100 dark:bg-red-900/30";
  if (color === "yellow") return "bg-amber-100 dark:bg-amber-900/30";
  return "bg-green-100 dark:bg-green-900/30";
};

const barTone = (color: string) =>
  color === "red" ? "bg-red-500" : color === "yellow" ? "bg-amber-500" : "bg-green-500";

const r1 = (n: number) => Math.round(n * 10) / 10;

const CapacityMonthly: React.FC<Props> = ({ data }) => {
  if (!data) {
    return (
      <div className="py-10 text-center text-sm text-gray-700 dark:text-gray-300">Carregando...</div>
    );
  }

  // Agrupa as semanas em meses (pela segunda-feira de cada semana).
  const monthKeys: number[] = [];
  const monthLabel: Record<number, string> = {};
  data.weeks.forEach((w) => {
    const d = new Date(w);
    const k = d.getFullYear() * 12 + d.getMonth();
    if (!(k in monthLabel)) {
      monthKeys.push(k);
      monthLabel[k] = d.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
    }
  });

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[760px] border-separate border-spacing-1">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-white px-2 text-left text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:bg-navy-800 dark:text-gray-300">
              Colaborador
            </th>
            {monthKeys.map((k) => (
              <th key={k} className="px-1 text-center text-[10px] font-medium uppercase text-gray-600 dark:text-gray-300">
                {monthLabel[k]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row) => {
            const agg: Record<number, MonthAgg> = {};
            row.cells.forEach((c, i) => {
              const d = new Date(data.weeks[i]);
              const k = d.getFullYear() * 12 + d.getMonth();
              const a = (agg[k] ??= { capacity: 0, allocated: 0, worked: 0, estimated: 0 });
              a.capacity += c.capacityHours;
              a.allocated += c.allocatedHours;
              a.worked += c.workedHours;
              a.estimated += c.estimatedHours;
            });

            return (
              <tr key={row.employeeId}>
                <td className="sticky left-0 z-10 bg-white px-2 dark:bg-navy-800">
                  <p className="text-xs font-semibold text-navy-700 dark:text-white">
                    {row.employeeName}
                  </p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">{row.weeklyCapacityHours}h/sem</p>
                </td>
                {monthKeys.map((k) => {
                  const a = agg[k] ?? { capacity: 0, allocated: 0, worked: 0, estimated: 0 };
                  const util = a.capacity > 0 ? (a.allocated / a.capacity) * 100 : 0;
                  const color = monthColor(util);
                  return (
                    <td key={k} className="p-0">
                      <div
                        className={`flex h-12 w-20 flex-col items-center justify-center rounded-md ${cellTone(a.allocated, a.worked, color)}`}
                        title={[
                          monthLabel[k],
                          `Capacity: ${r1(a.capacity)}h`,
                          `Alocado: ${r1(a.allocated)}h (${r1(util)}%)`,
                          `Estimado: ${r1(a.estimated)}h`,
                          `Trabalhado: ${r1(a.worked)}h`,
                        ].join("\n")}
                      >
                        <span className="text-[11px] font-semibold text-navy-700 dark:text-white">
                          {r1(a.allocated)}/{r1(a.capacity)}
                        </span>
                        <div className="mt-1 h-1 w-14 overflow-hidden rounded-full bg-white/60 dark:bg-navy-900">
                          <div
                            className={`h-full ${barTone(color)}`}
                            style={{ width: `${Math.min(100, util)}%` }}
                          />
                        </div>
                        {a.estimated > 0 && (
                          <span className="mt-0.5 text-[9px] font-medium text-dv-blue-600 dark:text-dv-cyan-300">
                            ~{r1(a.estimated)}h est
                          </span>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {data.rows.length === 0 && (
            <tr>
              <td className="px-3 py-10 text-center text-gray-700 dark:text-gray-300" colSpan={monthKeys.length + 1}>
                <span className="flex flex-col items-center gap-2">
                  <MdPeopleOutline aria-hidden="true" className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                  Nenhum colaborador ativo.
                </span>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-gray-700 dark:text-gray-300">
        <span className="flex items-center gap-1"><i aria-hidden="true" className="h-2.5 w-2.5 rounded-sm bg-green-500" /> &lt; 80%</span>
        <span className="flex items-center gap-1"><i aria-hidden="true" className="h-2.5 w-2.5 rounded-sm bg-amber-500" /> 80–99%</span>
        <span className="flex items-center gap-1"><i aria-hidden="true" className="h-2.5 w-2.5 rounded-sm bg-red-500" /> ≥ 100% (sobre-alocado)</span>
      </div>
      <p className="mt-1 text-[11px] text-gray-700 dark:text-gray-300">
        Cada célula: horas alocadas / capacity do mês (soma das semanas). Passe o mouse para o detalhe.
      </p>
    </div>
  );
};

export default CapacityMonthly;
